// src/pages/Claims.js
// Claims List UI - View and manage all claims

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useDemoTour } from '../contexts';
import { supabase } from '../lib/supabaseClient';
import * as awsApi from '../services/awsApiService';
import { logPHIAccess } from '../services/auditService';
import { demoDenialAnalysis, demoMedicalCoding, demoClaims } from '../data/demoData';
import * as apiService from '../services/apiService';
import {
  FileText,
  Search,
  DollarSign,
  Calendar,
  AlertCircle,
  RefreshCw,
  Upload,
  Filter,
  X,
  CheckCircle,
  Clock,
  XCircle,
  AlertTriangle,
  Eye,
  Send,
  ChevronDown,
  ChevronUp,
  Zap,
  Paperclip,
  Trash2,
  Download
} from 'lucide-react';

const useAWS = (process.env.REACT_APP_USE_AWS || '').trim() === 'true';

const Claims = () => {
  const navigate = useNavigate();
  const { user, practice } = useAuth();
  const { currentStep } = useDemoTour() || {};
  const [claims, setClaims] = useState([]);
  const [filteredClaims, setFilteredClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [error, setError] = useState(null);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    paid: 0,
    denied: 0,
    totalBilled: 0,
    totalPaid: 0
  });

  // Appeal state
  const [appealLoading, setAppealLoading] = useState(false);
  const [faxSending, setFaxSending] = useState(false);
  const [appealToast, setAppealToast] = useState(null);
  const [showLetter, setShowLetter] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const toastTimer = useRef(null);

  // Write-off state
  const [showWriteOff, setShowWriteOff] = useState(false);
  const [writeOffAmount, setWriteOffAmount] = useState('');
  const [writeOffReason, setWriteOffReason] = useState('');
  const [writeOffLoading, setWriteOffLoading] = useState(false);

  const [demoSubmitFlash, setDemoSubmitFlash] = useState(false);
  const [submissionStep, setSubmissionStep] = useState(0); // 0=idle 1=validating 2=transmitting 3=received 4=accepted
  const [demoReadyToSubmit, setDemoReadyToSubmit] = useState(false);

  // Office Ally submit state
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);

  // Fax state
  const [showFaxModal, setShowFaxModal] = useState(false);
  const [faxNumber, setFaxNumber] = useState('');
  const [faxLoading, setFaxLoading] = useState(false);
  const [faxResult, setFaxResult] = useState(null);

  // Document state
  const [claimDocuments, setClaimDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const docInputRef = useRef(null);

  useEffect(() => {
    loadClaims();
  }, [practice]);

  // Demo tour: drive all claims-page step transitions from here
  // deps: currentStep?.id + claims.length — length fires when claims first load, not on every setClaims
  useEffect(() => {
    if (apiService.getAppMode() !== 'demo') return;
    if (!currentStep) return;

    const id = currentStep.id;
    // Read Sarah's claim once at effect run time (closure is fine — claim identity doesn't change)
    const sarah = claims.find(c => c.claim_number === 'CLM-2026-0318');
    if (!sarah && claims.length === 0) return; // wait for claims to load

    // Step 2 — new claim arrives: show clean table, reset Sarah to Pending
    if (id === 'workflow-new-claim') {
      setSelectedClaim(null);
      setShowLetter(false);
      setAppealLoading(false);
      setClaims(prev => prev.map(c =>
        (c.claim_number === 'CLM-2026-0318' || c.patient_name?.includes('Mitchell'))
          ? { ...c, claim_status: 'Pending', denial_code: null, denial_reason: null, notes: null, claim_paid_amount: 0 }
          : c
      ));
      return;
    }

    // Step 3 — AI scrubs: open Sarah's claim with coding result
    if (id === 'workflow-scrub') {
      if (!sarah) return;
      const claimWithCoding = {
        ...sarah,
        automation_status: 'completed',
        ai_coding_result: {
          codingAccuracy: '94%',
          suggestedCodes: [
            { code: 'D2750', description: 'Crown – Porcelain Fused to Metal', confidence: 94, denialRisk: 'Low (6%)' },
            { code: 'D2950', description: 'Core Buildup', confidence: 91, denialRisk: 'Low (8%)' },
          ],
          recommendations: ['All required documentation attached and confirmed'],
        },
        claim_documents: [
          { name: 'Pre-Treatment X-Ray', type: 'image/jpeg', date: '2026-03-15', status: 'attached' },
          { name: 'Tooth Surface Notation', type: 'application/pdf', date: '2026-03-18', status: 'attached' },
          { name: 'Clinical Notes — D2750', type: 'application/pdf', date: '2026-03-18', status: 'attached' },
          { name: 'Prior Auth — ML-AUTH-334812', type: 'auth', date: '2026-03-13', status: 'confirmed' },
        ],
      };
      const t1 = setTimeout(() => { setSelectedClaim(null); setShowLetter(false); }, 200);
      const t2 = setTimeout(() => { setSelectedClaim(claimWithCoding); setShowLetter(false); setDemoReadyToSubmit(true); }, 900);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }

    // Step 4 — submitted: open claim detail with animated EDI transmission sequence
    if (id === 'workflow-submitted') {
      setShowLetter(false);
      setSubmissionStep(0);
      setDemoReadyToSubmit(false);
      const submittedClaim = {
        ...sarah,
        claim_status: 'Submitted',
        claim_paid_amount: 0,
        automation_status: 'completed',
        claim_documents: [
          { name: 'Pre-Treatment X-Ray', type: 'image/jpeg', date: '2026-03-15', status: 'attached' },
          { name: 'Tooth Surface Notation', type: 'application/pdf', date: '2026-03-18', status: 'attached' },
          { name: 'Clinical Notes — D2750', type: 'application/pdf', date: '2026-03-18', status: 'attached' },
          { name: 'Prior Auth — ML-AUTH-334812', type: 'auth', date: '2026-03-13', status: 'confirmed' },
        ],
        submission_confirmation: {
          method: 'EDI 837D',
          clearinghouse: 'Office Ally',
          transmitted_at: '2026-03-20T08:34:12.000Z',
          transaction_id: 'OA-2026-0318-8472',
          status: 'Accepted',
        },
      };
      setClaims(prev => prev.map(c =>
        (c.claim_number === 'CLM-2026-0318' || c.patient_name?.includes('Mitchell')) ? submittedClaim : c
      ));
      const t1 = setTimeout(() => { setSelectedClaim(submittedClaim); setSubmissionStep(1); }, 500);
      const t2 = setTimeout(() => setSubmissionStep(2), 1500);
      const t3 = setTimeout(() => setSubmissionStep(3), 2800);
      const t4 = setTimeout(() => {
        setSubmissionStep(4);
        setDemoSubmitFlash(true);
        setTimeout(() => setDemoSubmitFlash(false), 8000);
      }, 4000);
      return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); };
    }

    // Step 5 — payer denies: flip status to Denied, open claim detail
    if (id === 'workflow-denial') {
      if (!sarah) return;
      const deniedClaim = { ...sarah, claim_status: 'Denied', denial_code: 'CO-4', denial_reason: 'Procedure not covered as submitted', notes: 'Denied CO-4: procedure not covered as submitted', claim_paid_amount: 0 };
      const t1 = setTimeout(() => {
        setClaims(prev => prev.map(c =>
          (c.claim_number === 'CLM-2026-0318' || c.patient_name?.includes('Mitchell')) ? deniedClaim : c
        ));
      }, 600);
      const t2 = setTimeout(() => {
        setSelectedClaim(deniedClaim);
        setShowLetter(false);
      }, 1400);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }

    // Step 6 — AI writes appeal: open claim, generate letter, then auto-fax sequence
    if (id === 'workflow-appeal') {
      if (!sarah) return;
      const deniedClaim = { ...sarah, claim_status: 'Denied', denial_code: 'CO-4', denial_reason: 'Procedure not covered as submitted', notes: 'Denied CO-4: procedure not covered as submitted' };
      const appealDocs = [
        { name: 'Pre-Treatment X-Ray', type: 'image/jpeg', date: '2026-03-15' },
        { name: 'Clinical Notes — D2750', type: 'application/pdf', date: '2026-03-18' },
        { name: 'Treatment Plan', type: 'application/pdf', date: '2026-03-10' },
      ];
      const codingResult = {
        codingAccuracy: '94%',
        suggestedCodes: [
          { code: demoMedicalCoding.suggestedCodes.primary[0].code, description: demoMedicalCoding.suggestedCodes.primary[0].description, denialRisk: demoMedicalCoding.suggestedCodes.primary[0].denialRisk },
          { code: demoMedicalCoding.suggestedCodes.additional[1].code, description: demoMedicalCoding.suggestedCodes.additional[1].description, denialRisk: demoMedicalCoding.suggestedCodes.additional[1].denialRisk },
        ],
        recommendations: [demoMedicalCoding.complianceCheck.suggestions[0]],
      };
      // t1: select claim + show AI loading
      const t1 = setTimeout(() => { setSelectedClaim(deniedClaim); setAppealLoading(true); }, 400);
      // t2: AI finishes — show appeal letter (no fax yet)
      const t2 = setTimeout(() => {
        setAppealLoading(false);
        setSelectedClaim({ ...deniedClaim, appeal_letter: demoDenialAnalysis.suggestedAppealLanguage, appeal_submitted_date: '2026-04-02T09:30:00.000Z', appeal_deadline: '2026-05-01T09:30:00.000Z', appeal_status: 'Submitted', automation_status: 'completed', ai_coding_result: codingResult, fax_number: '(800) 638-8329', appeal_documents: appealDocs });
        setShowLetter(true);
        setFaxSending(false);
      }, 2500);
      // t3: auto-fax fires — show "Faxing..." spinner
      const t3 = setTimeout(() => setFaxSending(true), 4000);
      // t4: fax confirmed — replace spinner with confirmed badge
      const t4 = setTimeout(() => {
        setFaxSending(false);
        setSelectedClaim(prev => prev ? { ...prev, fax_confirmed: true } : prev);
      }, 6000);
      return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); setFaxSending(false); };
    }

    // Feature step: claims-table — close modal, show clean table
    if (id === 'claims-table') {
      setSelectedClaim(null);
      setShowLetter(false);
      setAppealLoading(false);
      return;
    }

    // Feature step: denied-claims — open James Torres as example denied claim (not Sarah, who paid clean)
    if (id === 'denied-claims') {
      const torres = claims.find(c => c.claim_number === 'CLM-2026-0315');
      if (!torres) return;
      const deniedTorres = { ...torres, denial_code: 'CO-29', denial_reason: 'Services not covered for frequency', notes: 'Denied CO-29: D3330 billed within frequency limitation period. Prior root canal on record within 24 months.', claim_paid_amount: 0 };
      const t = setTimeout(() => { setSelectedClaim(deniedTorres); setShowLetter(false); }, 500);
      return () => clearTimeout(t);
    }

    // Feature step: appeal-button — open James Torres with AI-written appeal letter
    if (id === 'appeal-button') {
      const torres = claims.find(c => c.claim_number === 'CLM-2026-0315');
      if (!torres) return;
      const torresWithAppeal = {
        ...torres,
        denial_code: 'CO-29',
        denial_reason: 'Services not covered for frequency',
        notes: 'Denied CO-29: D3330 billed within frequency limitation period.',
        claim_paid_amount: 0,
        appeal_letter: demoDenialAnalysis.suggestedAppealLanguage,
        appeal_submitted_date: '2026-04-05T10:15:00.000Z',
        appeal_deadline: '2026-05-05T10:15:00.000Z',
        appeal_status: 'Submitted',
        automation_status: 'completed',
        fax_confirmed: true,
        fax_number: '(800) 932-0783',
        appeal_documents: [
          { name: 'Periapical X-Ray — Tooth #19', type: 'image/jpeg', date: '2026-03-10' },
          { name: 'Clinical Notes — D3330', type: 'application/pdf', date: '2026-03-15' },
          { name: 'Prior Treatment History', type: 'application/pdf', date: '2026-03-15' },
        ],
      };
      const t = setTimeout(() => { setSelectedClaim(torresWithAppeal); setShowLetter(true); }, 500);
      return () => clearTimeout(t);
    }

    // Feature step: appeal-approved — James Torres appeal wins, $420 posted
    if (id === 'appeal-approved') {
      const torres = claims.find(c => c.claim_number === 'CLM-2026-0315');
      if (!torres) return;
      const approvedTorres = {
        ...torres,
        claim_status: 'Paid',
        claim_paid_amount: 420,
        automation_status: 'completed',
        denial_code: 'CO-29',
        denial_reason: 'Services not covered for frequency',
        appeal_status: 'Approved',
        appeal_submitted_date: '2026-04-05T10:15:00.000Z',
        appeal_approved_date: '2026-04-26T14:22:00.000Z',
        appeal_letter: demoDenialAnalysis.suggestedAppealLanguage,
        fax_confirmed: true,
        fax_number: '(800) 932-0783',
        notes: 'Appeal approved — Delta Dental reversed CO-29 denial. D3330 covered. $420 paid.',
      };
      setClaims(prev => prev.map(c => c.claim_number === 'CLM-2026-0315' ? approvedTorres : c));
      const t = setTimeout(() => { setSelectedClaim(approvedTorres); setShowLetter(false); }, 500);
      return () => clearTimeout(t);
    }

    // Feature step: appeal-attachments — open claim with appeal + bundled documents
    if (id === 'appeal-attachments') {
      if (!sarah) return;
      const claimWithDocs = {
        ...sarah,
        claim_status: 'Denied',
        denial_code: 'CO-4',
        denial_reason: 'Procedure not covered as submitted',
        notes: 'Denied CO-4: procedure not covered as submitted',
        claim_paid_amount: 0,
        appeal_letter: demoDenialAnalysis.suggestedAppealLanguage,
        appeal_submitted_date: '2026-04-02T09:30:00.000Z',
        appeal_deadline: '2026-05-01T09:30:00.000Z',
        appeal_status: 'Submitted',
        automation_status: 'completed',
        appeal_documents: [
          { name: 'Pre-Treatment X-Ray', type: 'image/jpeg', date: '2026-03-15' },
          { name: 'Clinical Notes — D2750', type: 'application/pdf', date: '2026-03-18' },
          { name: 'Treatment Plan', type: 'application/pdf', date: '2026-03-10' },
        ],
      };
      const t = setTimeout(() => {
        setSelectedClaim(claimWithDocs);
        setShowLetter(false);
      }, 500);
      return () => clearTimeout(t);
    }

    // Feature step: claim-coding — open claim with AI coding result
    if (id === 'claim-coding') {
      if (!sarah) return;
      const claimWithCoding = {
        ...sarah,
        automation_status: 'completed',
        ai_coding_result: {
          codingAccuracy: '98%',
          suggestedCodes: [
            { code: 'D3330', description: 'Molar Root Canal', confidence: 98, denialRisk: 'Low (2%)' },
            { code: 'D2950', description: 'Core Buildup', confidence: 94, denialRisk: 'Low (6%)' },
          ],
          recommendations: ['Bundling rules verified — add-on code approved for this payer'],
        },
      };
      const t1 = setTimeout(() => { setSelectedClaim(null); setShowLetter(false); }, 100);
      const t2 = setTimeout(() => { setSelectedClaim(claimWithCoding); setShowLetter(false); }, 700);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [currentStep?.id, claims.length]); // eslint-disable-line

  useEffect(() => {
    let filtered = claims;

    if (statusFilter !== 'all') {
      filtered = filtered.filter(c => c.claim_status?.toLowerCase() === statusFilter.toLowerCase());
    }

    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(c =>
        (c.open_dental_claim_id?.toLowerCase() || '').includes(term) ||
        ((c.payer_name || c.payer)?.toLowerCase() || '').includes(term) ||
        (c.patient_name?.toLowerCase() || '').includes(term) ||
        (c.procedure_codes?.join(' ')?.toLowerCase() || '').includes(term)
      );
    }

    setFilteredClaims(filtered);
  }, [searchTerm, statusFilter, claims]);

  const loadClaims = async () => {
    setLoading(true);
    setError(null);

    // Demo mode — always use consistent demo claims data
    if (apiService.getAppMode() === 'demo') {
      const data = demoClaims;
      setClaims(data);
      setFilteredClaims(data);
      const totalBilled = data.reduce((sum, c) => sum + (parseFloat(c.claim_amount) || 0), 0);
      const totalPaid = data.reduce((sum, c) => sum + (parseFloat(c.claim_paid_amount) || 0), 0);
      setStats({
        total: data.length,
        pending: data.filter(c => ['Pending', 'Submitted', 'In Review'].includes(c.claim_status)).length,
        paid: data.filter(c => c.claim_status === 'Paid').length,
        denied: data.filter(c => c.claim_status === 'Denied').length,
        needsReview: data.filter(c => c.claim_status === 'AI Flagged').length,
        totalBilled,
        totalPaid,
      });
      setLoading(false);
      return;
    }

    const practiceId = useAWS ? practice?.practice_id : practice?.id;
    if (!practiceId && !useAWS) {
      setLoading(false);
      return;
    }

    try {
      let data = [];

      if (useAWS) {
        const result = await awsApi.getClaims(practiceId, { limit: 200 });
        if (result.success) {
          data = (result.data || []).map(claim => ({
            ...claim,
            patient_name: claim.patient_first_name
              ? `${claim.patient_first_name || ''} ${claim.patient_last_name || ''}`.trim()
              : 'Unknown Patient'
          }));
        } else {
          throw new Error(result.error || 'Failed to load claims');
        }
      } else {
        const { data: supabaseData, error: queryError } = await supabase
          .from('claims')
          .select(`*, patients:patient_id (first_name, last_name)`)
          .eq('practice_id', practiceId)
          .order('created_at', { ascending: false })
          .limit(200);

        if (queryError) throw new Error(queryError.message);
        data = (supabaseData || []).map(claim => ({
          ...claim,
          patient_name: claim.patients
            ? `${claim.patients.first_name || ''} ${claim.patients.last_name || ''}`.trim()
            : 'Unknown Patient'
        }));
      }

      setClaims(data);
      setFilteredClaims(data);
      logPHIAccess('claim_list', null, 'view');

      const totalBilled = data.reduce((sum, c) => sum + (parseFloat(c.claim_amount) || 0), 0);
      const totalPaid = data.reduce((sum, c) => sum + (parseFloat(c.claim_paid_amount) || 0), 0);
      const pending = data.filter(c => ['Pending', 'Submitted', 'In Review'].includes(c.claim_status)).length;
      const needsReview = data.filter(c => c.claim_status === 'AI Flagged').length;
      const paid = data.filter(c => c.claim_status === 'Paid').length;
      const denied = data.filter(c => c.claim_status === 'Denied').length;

      setStats({ total: data.length, pending, paid, denied, needsReview, totalBilled, totalPaid });
    } catch (err) {
      setError(err.message || 'Query timeout - please refresh the page');
      setClaims([]);
      setFilteredClaims([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { timeZone: 'UTC' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const daysUntil = (dateStr) => {
    if (!dateStr) return null;
    const diff = new Date(dateStr) - new Date();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const getStatusBadge = (status) => {
    const config = {
      'Paid': { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
      'Pending': { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock },
      'Submitted': { bg: 'bg-blue-100', text: 'text-blue-700', icon: Clock },
      'In Review': { bg: 'bg-blue-100', text: 'text-blue-700', icon: Clock },
      'Denied': { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
      'Appealed': { bg: 'bg-orange-100', text: 'text-orange-700', icon: AlertTriangle },
      'AI Flagged': { bg: 'bg-amber-100', text: 'text-amber-700', icon: AlertTriangle }
    }[status] || { bg: 'bg-gray-100', text: 'text-gray-700', icon: FileText };

    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.text}`}>
        <Icon className="w-3 h-3" />
        {status || 'Unknown'}
      </span>
    );
  };

  const getAppealStatusBadge = (appealStatus) => {
    const config = {
      'Submitted': { bg: 'bg-orange-100', text: 'text-orange-700' },
      'Approved': { bg: 'bg-green-100', text: 'text-green-700' },
      'Denied': { bg: 'bg-red-100', text: 'text-red-700' },
      'Partially Approved': { bg: 'bg-teal-100', text: 'text-teal-700' }
    }[appealStatus] || { bg: 'bg-gray-100', text: 'text-gray-700' };

    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        Appeal: {appealStatus}
      </span>
    );
  };

  const showToast = (msg) => {
    setAppealToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setAppealToast(null), 4000);
  };

  const handleCloseModal = () => {
    setSelectedClaim(null);
    setAppealLoading(false);
    setShowLetter(false);
    setStatusUpdating(false);
    setShowFaxModal(false);
    setFaxNumber('');
    setFaxResult(null);
    setClaimDocuments([]);
  };

  // Load documents when a claim is selected
  useEffect(() => {
    if (!selectedClaim?.id) { setClaimDocuments([]); return; }
    setDocsLoading(true);
    awsApi.listClaimDocuments(selectedClaim.id).then(res => {
      setClaimDocuments(res.documents || []);
    }).catch(() => {}).finally(() => setDocsLoading(false));
  }, [selectedClaim?.id]);

  const handleDocumentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedClaim) return;
    e.target.value = '';
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const base64 = ev.target.result.split(',')[1];
        const docType = file.type.startsWith('image/') ? 'X-Ray / Image'
          : file.name.toLowerCase().includes('eob') ? 'EOB'
          : file.name.toLowerCase().includes('auth') ? 'Pre-Auth'
          : 'Supporting Document';
        const res = await awsApi.uploadDocument({
          claimId: selectedClaim.id,
          documentType: docType,
          fileName: file.name,
          fileData: base64,
          mimeType: file.type,
        });
        setUploading(false);
        if (res.success) {
          setClaimDocuments(prev => [res.data.document, ...prev]);
          showToast('Document uploaded');
        } else {
          showToast(res.error || 'Upload failed', 'error');
        }
      };
      reader.readAsDataURL(file);
    } catch {
      setUploading(false);
      showToast('Upload failed', 'error');
    }
  };

  const handleDownloadDocument = async (doc) => {
    const res = await awsApi.getDocumentUrl(doc.id);
    if (res.success) window.open(res.url, '_blank');
    else showToast(res.error || 'Could not generate download link', 'error');
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    const res = await awsApi.deleteDocument(docId);
    if (res.success) setClaimDocuments(prev => prev.filter(d => d.id !== docId));
    else showToast(res.error || 'Delete failed', 'error');
  };

  const handleSendFax = async () => {
    if (!selectedClaim) return;
    setFaxLoading(true);
    setFaxResult(null);
    const result = await awsApi.sendAppealFax(selectedClaim.id, faxNumber || null);
    setFaxLoading(false);
    if (result.success) {
      setFaxResult({ success: true, message: result.data?.message || 'Fax sent successfully!' });
      setShowFaxModal(false);
      showToast(`Appeal faxed to ${result.data?.faxNumber || faxNumber}`);
    } else {
      setFaxResult({ success: false, message: result.error || 'Fax failed. Please verify the fax number.' });
    }
  };

  const handleDryRun = async () => {
    if (!selectedClaim) return;
    setSubmitLoading(true);
    setSubmitResult(null);
    try {
      const result = await awsApi.submitClaimToOfficeAlly(selectedClaim.id, true);
      if (result.success) {
        setSubmitResult({ success: true, message: 'Dry run complete — EDI logged to CloudWatch. No claim was submitted.' + (result.edi_preview ? '\n\nEDI preview:\n' + result.edi_preview : '') });
      } else {
        setSubmitResult({ success: false, message: result.error || result.details || 'Dry run failed' });
      }
    } catch (err) {
      setSubmitResult({ success: false, message: err.message });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleSubmitToInsurance = async () => {
    if (!selectedClaim) return;
    setSubmitLoading(true);
    setSubmitResult(null);
    try {
      const result = await awsApi.submitClaimToOfficeAlly(selectedClaim.id);
      if (result.success) {
        setSubmitResult({ success: true, message: result.message || 'Claim submitted successfully' });
        setClaims(prev => prev.map(c => c.id === selectedClaim.id ? { ...c, claim_status: 'Submitted' } : c));
        setSelectedClaim(prev => ({ ...prev, claim_status: 'Submitted' }));
      } else {
        setSubmitResult({ success: false, message: result.error || result.details || 'Submission failed' });
      }
    } catch (err) {
      setSubmitResult({ success: false, message: err.message || 'Submission failed' });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleSubmitAppeal = async () => {
    if (!selectedClaim) return;
    setAppealLoading(true);
    setError(null);

    const result = await awsApi.submitAppeal(selectedClaim.id);
    setAppealLoading(false);

    if (result.success) {
      const updatedClaim = {
        ...selectedClaim,
        ...result.data.claim,
        patient_name: selectedClaim.patient_name
      };
      setSelectedClaim(updatedClaim);
      setClaims(prev => prev.map(c => c.id === updatedClaim.id ? updatedClaim : c));
      showToast('Appeal submitted successfully. Claim status updated to Appealed.');
    } else {
      setError(result.error || 'Failed to submit appeal');
    }
  };

  const handleWriteOff = async () => {
    if (!selectedClaim) return;
    setWriteOffLoading(true);
    const res = await awsApi.writeOffClaim(selectedClaim.id, parseFloat(writeOffAmount) || 0, writeOffReason || 'Write-off');
    setWriteOffLoading(false);
    if (res.success) {
      setClaims(prev => prev.map(c => c.id === selectedClaim.id ? { ...c, claim_status: 'Written Off' } : c));
      setSelectedClaim(prev => ({ ...prev, claim_status: 'Written Off' }));
      setShowWriteOff(false);
      setWriteOffAmount('');
      setWriteOffReason('');
      showToast('Claim written off');
    } else {
      showToast(res.error || 'Write-off failed', 'error');
    }
  };

  const handleUpdateAppealStatus = async (newStatus) => {
    if (!selectedClaim) return;
    setStatusUpdating(true);

    const result = await awsApi.updateAppealStatus(selectedClaim.id, newStatus);
    setStatusUpdating(false);

    if (result.success) {
      const updatedClaim = {
        ...selectedClaim,
        ...result.data.claim,
        patient_name: selectedClaim.patient_name
      };
      setSelectedClaim(updatedClaim);
      setClaims(prev => prev.map(c => c.id === updatedClaim.id ? updatedClaim : c));
      showToast(`Appeal status updated to ${newStatus}.`);
    } else {
      setError(result.error || 'Failed to update appeal status');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Claims Pipeline</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const rows = filteredClaims.map(c => [
                c.claim_number || '', c.patient_name || '', c.payer || c.payer_name || '',
                c.claim_status || '', c.claim_amount || '', c.claim_paid_amount || '',
                c.service_date ? new Date(c.service_date).toLocaleDateString() : '',
                c.denial_code || '',
                Array.isArray(c.procedure_codes) ? c.procedure_codes.join(';') : (c.procedure_codes || '')
              ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));
              const csv = ['Claim #,Patient,Payer,Status,Amount,Paid,Service Date,Denial Code,Procedures', ...rows].join('\n');
              const a = document.createElement('a');
              a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
              a.download = `claims-${new Date().toISOString().split('T')[0]}.csv`;
              a.click();
            }}
            className="flex items-center gap-2 border border-slate-300 text-slate-600 hover:bg-slate-50 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
          <button onClick={() => navigate('/data-import')} className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg font-medium transition-colors">
            <Upload className="w-4 h-4" /> Import Claims
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div data-tour="claims-stats-row" className="grid grid-cols-6 gap-4 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-4 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <p className="text-slate-600 text-xs">Total Claims</p>
                <p className="text-xl font-bold text-slate-900">{stats.total}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-slate-600 text-xs">Pending</p>
                <p className="text-xl font-bold text-slate-900">{stats.pending}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-slate-600 text-xs">Paid</p>
                <p className="text-xl font-bold text-slate-900">{stats.paid}</p>
              </div>
            </div>
          </div>
          <div data-tour="denied-claims-stat" className="bg-white rounded-xl shadow-sm p-4 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-slate-600 text-xs">Denied</p>
                <p className="text-xl font-bold text-slate-900">{stats.denied}</p>
              </div>
            </div>
          </div>
          {stats.needsReview > 0 && (
            <div className="bg-amber-50 rounded-xl shadow-sm p-4 border border-amber-300 cursor-pointer" onClick={() => setStatusFilter('ai flagged')}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-amber-700 text-xs font-semibold">AI Flagged</p>
                  <p className="text-xl font-bold text-amber-800">{stats.needsReview}</p>
                </div>
              </div>
            </div>
          )}
          <div className="bg-white rounded-xl shadow-sm p-4 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-slate-600 text-xs">Total Billed</p>
                <p className="text-lg font-bold text-slate-900">{formatCurrency(stats.totalBilled)}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-slate-600 text-xs">Total Paid</p>
                <p className="text-lg font-bold text-green-700">{formatCurrency(stats.totalPaid)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Step 4 demo flash — clearinghouse submission confirmation */}
        {demoSubmitFlash && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-green-50 border border-green-200 flex items-center gap-3" style={{ animation: 'fadeInDown 0.4s ease' }}>
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-green-800">Claim sent to Office Ally clearinghouse</p>
              <p className="text-xs text-green-600">EDI 837D file transmitted automatically · No portal login required</p>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 rounded-lg border bg-red-50 border-red-200 text-red-700">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              <span className="font-medium">{error}</span>
              <button onClick={loadClaims} className="ml-auto flex items-center gap-1 text-red-600 hover:text-red-800">
                <RefreshCw className="w-4 h-4" /> Retry
              </button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-6">
          <div className="p-4 flex gap-4">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search by claim ID, patient name, payer, or procedure code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div className="relative">
              <Filter className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="pl-10 pr-8 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent appearance-none bg-white"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="submitted">Submitted</option>
                <option value="paid">Paid</option>
                <option value="denied">Denied</option>
                <option value="appealed">Appealed</option>
                <option value="ai flagged">AI Flagged</option>
              </select>
            </div>
          </div>
        </div>

        <div data-tour="claims-table" className="bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="p-6 border-b border-slate-200 flex items-center justify-between">
            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <FileText className="w-5 h-5 text-teal-600" /> Claims ({filteredClaims.length})
            </h2>
            <button onClick={loadClaims} className="flex items-center gap-2 text-slate-600 hover:text-slate-800">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="text-slate-600 mt-4">Loading claims...</p>
            </div>
          ) : filteredClaims.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              {searchTerm || statusFilter !== 'all' ? (
                <>
                  <p className="text-slate-600 font-medium">No claims match your filters</p>
                  <button onClick={() => { setSearchTerm(''); setStatusFilter('all'); }} className="mt-4 px-4 py-2 text-teal-600 hover:text-teal-700 font-medium">
                    Clear Filters
                  </button>
                </>
              ) : (
                <>
                  <p className="text-slate-600 font-medium">No claims yet</p>
                  <button onClick={() => navigate('/data-import')} className="mt-4 px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors">
                    Import Claims
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-3 px-6 text-sm font-semibold text-slate-600">Claim #</th>
                    <th className="text-left py-3 px-6 text-sm font-semibold text-slate-600">Patient</th>
                    <th className="text-left py-3 px-6 text-sm font-semibold text-slate-600">Payer</th>
                    <th className="text-left py-3 px-6 text-sm font-semibold text-slate-600">Service Date</th>
                    <th className="text-right py-3 px-6 text-sm font-semibold text-slate-600">Amount</th>
                    <th className="text-right py-3 px-6 text-sm font-semibold text-slate-600">Paid</th>
                    <th className="text-left py-3 px-6 text-sm font-semibold text-slate-600">Status</th>
                    <th className="text-right py-3 px-6 text-sm font-semibold text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClaims.map((claim) => {
                    const deadlineDays = daysUntil(claim.appeal_deadline);
                    const deadlineWarning = deadlineDays !== null && deadlineDays <= 14 && deadlineDays >= 0;
                    return (
                      <tr
                        key={claim.id}
                        data-tour={apiService.getAppMode() === 'demo' && claim.claim_number === 'CLM-2026-0318' ? 'sarah-claim-row' : undefined}
                        className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                      >
                        <td className="py-4 px-6">
                          <span className="font-mono text-sm text-slate-900">{claim.claim_number || claim.open_dental_claim_id || claim.id?.slice(0, 8)}</span>
                        </td>
                        <td className="py-4 px-6 font-medium text-slate-900">{claim.patient_name}</td>
                        <td className="py-4 px-6 text-slate-700">{claim.payer_name || claim.payer || 'N/A'}</td>
                        <td className="py-4 px-6 text-slate-700">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-slate-400" />
                            {formatDate(claim.service_date)}
                          </div>
                        </td>
                        <td className="py-4 px-6 text-right font-medium text-slate-900">{formatCurrency(claim.claim_amount)}</td>
                        <td className="py-4 px-6 text-right font-medium text-green-700">{formatCurrency(claim.claim_paid_amount)}</td>
                        <td data-tour={apiService.getAppMode() === 'demo' && claim.claim_number === 'CLM-2026-0318' ? 'sarah-status-badge' : undefined} className="py-4 px-6">
                          <div className="flex flex-col gap-1">
                            {getStatusBadge(claim.claim_status)}
                            {claim.appeal_status && getAppealStatusBadge(claim.appeal_status)}
                            {deadlineWarning && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                                <AlertTriangle className="w-3 h-3" />
                                Deadline in {deadlineDays}d
                              </span>
                            )}
                            {claim.requires_manual_portal && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                <AlertTriangle className="w-3 h-3" />
                                Portal Required
                              </span>
                            )}
                            {claim.automation_status === 'processing' && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-teal-50 text-teal-600">
                                <div className="w-2.5 h-2.5 border border-teal-400 border-t-transparent rounded-full animate-spin" />
                                Robot working...
                              </span>
                            )}
                            {claim.automation_status === 'completed' && claim.scrub_result && !claim.scrub_result.error && (
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                claim.scrub_result.riskLevel === 'High' ? 'bg-red-100 text-red-700' :
                                claim.scrub_result.riskLevel === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-green-100 text-green-700'
                              }`}>
                                🤖 {claim.scrub_result.riskLevel || 'Low'} Risk
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-6 text-right">
                          <button onClick={() => { setSelectedClaim(claim); setShowLetter(false); }} className="p-2 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" title="View Details">
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Toast notification */}
      {appealToast && (
        <div className="fixed bottom-6 right-6 z-50 bg-teal-700 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-3 max-w-sm">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm font-medium">{appealToast}</span>
          <button onClick={() => setAppealToast(null)} className="ml-auto text-teal-200 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {selectedClaim && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50">
          <div data-tour="claim-detail" className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between bg-white flex-shrink-0">
              <h2 className="text-2xl font-bold text-slate-900">Claim Details</h2>
              <button onClick={handleCloseModal} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1">
            {/* Demo fax status — sticky below header so always visible even when modal is scrolled */}
            {apiService.getAppMode() === 'demo' && (faxSending || selectedClaim?.fax_confirmed) && (
              <div className="px-6 py-3 bg-white sticky top-[73px] z-10 border-b border-purple-100">
                {faxSending && (
                  <div className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-lg px-4 py-3" style={{ animation: 'demoBadgeIn 0.4s ease-out' }}>
                    <RefreshCw className="w-4 h-4 text-purple-500 animate-spin flex-shrink-0" />
                    <p className="text-sm text-purple-700">
                      Auto-faxing appeal + {(selectedClaim?.appeal_documents || []).length} attachments to {selectedClaim?.payer_name || 'MetLife'}...
                    </p>
                  </div>
                )}
                {selectedClaim?.fax_confirmed && (
                  <div className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-lg px-4 py-3" style={{ animation: 'demoBadgeIn 0.6s ease-out, pulseGlowPurple 2s ease-in-out 0.6s infinite' }}>
                    <Send className="w-4 h-4 text-purple-600 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-purple-800">✓ Auto-Faxed with Documents Attached</p>
                      <p className="text-xs text-purple-600">Faxed to {selectedClaim.payer_name || 'MetLife'} appeals dept · {selectedClaim.fax_number} · {(selectedClaim.appeal_documents || []).length} docs attached</p>
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Claim ID</p>
                  <p className="font-mono font-medium text-slate-900">{selectedClaim.open_dental_claim_id || selectedClaim.id}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Status</p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {getStatusBadge(selectedClaim.claim_status)}
                    {selectedClaim.appeal_status && getAppealStatusBadge(selectedClaim.appeal_status)}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Patient</p>
                  <p className="font-medium text-slate-900">{selectedClaim.patient_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Payer</p>
                  <p className="font-medium text-slate-900">{selectedClaim.payer_name || selectedClaim.payer || 'N/A'}</p>
                </div>
              </div>

              {selectedClaim.scrub_risk_score !== null && selectedClaim.scrub_risk_score !== undefined && (
                <div className={`rounded-lg p-4 border ${selectedClaim.scrub_risk_score >= 70 ? 'bg-red-50 border-red-200' : selectedClaim.scrub_risk_score >= 40 ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-teal-600" />
                      Auto-Scrub Results
                    </h3>
                    <div className={`px-3 py-1 rounded-full text-sm font-bold ${selectedClaim.scrub_risk_score >= 70 ? 'bg-red-600 text-white' : selectedClaim.scrub_risk_score >= 40 ? 'bg-yellow-500 text-white' : 'bg-green-600 text-white'}`}>
                      {selectedClaim.scrub_risk_level || 'Unknown'} Risk — {selectedClaim.scrub_risk_score}/100
                    </div>
                  </div>
                  {selectedClaim.scrub_issues && selectedClaim.scrub_issues.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {selectedClaim.scrub_issues.map((issue, i) => (
                        <div key={i} className="text-sm text-slate-700">⚠ {issue.title} — {issue.fix}</div>
                      ))}
                    </div>
                  )}
                  {(!selectedClaim.scrub_issues || selectedClaim.scrub_issues.length === 0) && (
                    <div className="text-sm text-green-700 mt-1">✓ No issues detected — claim looks clean</div>
                  )}
                </div>
              )}

              {/* Claim Robot Auto-Processing Panel */}
              {selectedClaim.automation_status && (
                <div className="rounded-lg border border-teal-200 overflow-hidden">
                  <div className="bg-teal-600 px-4 py-2.5 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-2" />
                      </svg>
                      <span className="text-white font-semibold text-sm">Claim Robot</span>
                    </div>
                    {selectedClaim.automation_status === 'processing' && (
                      <div className="flex items-center gap-2 text-teal-100 text-xs">
                        <div className="w-3 h-3 border-2 border-teal-200 border-t-transparent rounded-full animate-spin" />
                        Processing...
                      </div>
                    )}
                    {selectedClaim.automation_status === 'completed' && (
                      <span className="text-xs text-teal-100">
                        Processed {selectedClaim.auto_processed_at ? new Date(selectedClaim.auto_processed_at).toLocaleDateString() : ''}
                      </span>
                    )}
                    {selectedClaim.automation_status === 'failed' && (
                      <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Processing failed</span>
                    )}
                  </div>

                  {selectedClaim.automation_status === 'completed' && (
                    <div className="p-4 bg-teal-50 space-y-4">
                      {/* Claim Scrub */}
                      {selectedClaim.scrub_result && !selectedClaim.scrub_result.error && (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-semibold text-slate-700">Claim Scrub</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                              selectedClaim.scrub_result.riskLevel === 'High' ? 'bg-red-100 text-red-700' :
                              selectedClaim.scrub_result.riskLevel === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {selectedClaim.scrub_result.riskLevel || 'Unknown'} Risk
                              {selectedClaim.scrub_result.riskScore != null ? ` — ${selectedClaim.scrub_result.riskScore}/100` : ''}
                            </span>
                          </div>
                          {selectedClaim.scrub_result.issues?.length > 0 ? (
                            <div className="space-y-1">
                              {selectedClaim.scrub_result.issues.slice(0, 3).map((issue, i) => (
                                <div key={i} className="text-xs text-slate-600 flex gap-1">
                                  <span className={issue.severity === 'High' ? 'text-red-500' : issue.severity === 'Medium' ? 'text-yellow-500' : 'text-slate-400'}>⚠</span>
                                  <span><strong>{issue.code || issue.description}</strong>{issue.fix ? ` — ${issue.fix}` : ''}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-green-700">✓ No issues — claim looks clean</p>
                          )}
                          {selectedClaim.scrub_result.recommendation && (
                            <p className="text-xs text-slate-500 mt-1 italic">{selectedClaim.scrub_result.recommendation}</p>
                          )}
                        </div>
                      )}

                      {/* AI Coding */}
                      {selectedClaim.ai_coding_result && !selectedClaim.ai_coding_result.error && (
                        <div data-tour="claim-coding">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-semibold text-slate-700">AI Coding</span>
                            {selectedClaim.ai_coding_result.codingAccuracy && (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-700">
                                {selectedClaim.ai_coding_result.codingAccuracy} Accuracy
                              </span>
                            )}
                          </div>
                          {selectedClaim.ai_coding_result.suggestedCodes?.length > 0 && (
                            <div className="space-y-1">
                              {selectedClaim.ai_coding_result.suggestedCodes.slice(0, 3).map((c, i) => (
                                <div key={i} className="text-xs text-slate-600 flex items-center gap-2">
                                  <span className="font-mono bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded">{c.code}</span>
                                  <span>{c.description}</span>
                                  {c.denialRisk && <span className={`ml-auto ${c.denialRisk?.includes('Low') ? 'text-green-600' : 'text-yellow-600'}`}>{c.denialRisk} denial risk</span>}
                                </div>
                              ))}
                            </div>
                          )}
                          {selectedClaim.ai_coding_result.recommendations?.length > 0 && (
                            <p className={`text-xs mt-1 ${selectedClaim.ai_coding_result.recommendations[0].startsWith('All required') ? 'text-green-600 font-medium' : 'text-slate-500'}`}>
                              {selectedClaim.ai_coding_result.recommendations[0].startsWith('All required') ? '✓' : '💡'} {selectedClaim.ai_coding_result.recommendations[0]}
                            </p>
                          )}
                          {selectedClaim.claim_documents?.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {selectedClaim.claim_documents.map((doc, i) => (
                                <div key={i} className="flex items-center gap-2 text-xs text-green-700 bg-green-50 rounded px-2 py-1">
                                  <span className="font-bold text-green-500">✓</span>
                                  <span className="flex-1">{doc.name}</span>
                                  <span className="text-green-500 font-medium">{doc.status}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {demoReadyToSubmit && (
                            <div className="mt-3">
                              <button
                                data-tour="demo-submit-button"
                                className="w-full py-2 px-4 rounded-lg text-sm font-semibold text-white"
                                style={{ background: 'linear-gradient(to right, #0d9488, #0f766e)', boxShadow: '0 2px 8px rgba(13,148,136,0.35)', animation: 'demoBadgeIn 0.4s ease-out' }}
                                onClick={() => {}}
                              >
                                Submit Claim via Office Ally
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Submission Confirmation — animated EDI transmission */}
                      {selectedClaim.submission_confirmation && (
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-semibold text-slate-700">Clearinghouse Transmission</span>
                            {submissionStep >= 4 && (
                              <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700" style={{ animation: 'demoBadgeIn 0.4s ease-out' }}>✓ Accepted</span>
                            )}
                          </div>
                          <div className="space-y-2">
                            {[
                              { step: 1, label: 'Validating codes and documentation' },
                              { step: 2, label: `Transmitting ${selectedClaim.submission_confirmation.method} to ${selectedClaim.submission_confirmation.clearinghouse}` },
                              { step: 3, label: `${selectedClaim.submission_confirmation.clearinghouse} received — forwarding to payer` },
                              { step: 4, label: `${selectedClaim.payer_name || 'MetLife'} accepted — claim in queue` },
                            ].map(({ step, label }) => (
                              <div key={step} className={`flex items-center gap-2 text-xs rounded px-2 py-1.5 transition-all duration-500 ${submissionStep >= step ? 'bg-green-50 text-green-700' : 'bg-slate-50 text-slate-400'}`}>
                                {submissionStep > step
                                  ? <span className="text-green-500 font-bold flex-shrink-0">✓</span>
                                  : submissionStep === step
                                    ? <div className="w-3 h-3 border-2 border-teal-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                                    : <div className="w-3 h-3 rounded-full bg-slate-200 flex-shrink-0" />
                                }
                                <span>{label}</span>
                              </div>
                            ))}
                          </div>
                          {submissionStep >= 4 && (
                            <>
                              <div className="mt-2 text-xs text-slate-500 space-y-0.5">
                                <p>🔖 {selectedClaim.submission_confirmation.transaction_id}</p>
                                <p>🕐 {new Date(selectedClaim.submission_confirmation.transmitted_at).toLocaleString()}</p>
                              </div>
                              {selectedClaim.claim_documents?.length > 0 && (
                                <div className="mt-2 space-y-1">
                                  {selectedClaim.claim_documents.map((doc, i) => (
                                    <div key={i} className="flex items-center gap-2 text-xs text-green-700 bg-green-50 rounded px-2 py-1" style={{ animation: 'demoBadgeIn 0.3s ease-out' }}>
                                      <span className="font-bold text-green-500">✓</span>
                                      <span className="flex-1">{doc.name}</span>
                                      <span className="text-green-500 font-medium">{doc.status}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}

                      {/* Pre-Auth */}
                      {selectedClaim.preauth_result && !selectedClaim.preauth_result.error && (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-semibold text-slate-700">Pre-Auth Check</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${selectedClaim.preauth_result.priorAuthRequired ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
                              {selectedClaim.preauth_result.priorAuthRequired ? 'Auth Required' : 'No Auth Needed'}
                            </span>
                          </div>
                          {selectedClaim.preauth_result.priorAuthRequired && (
                            <div className="text-xs text-slate-600 space-y-1">
                              {selectedClaim.preauth_result.estimatedApprovalTime && (
                                <p>⏱ Estimated approval: {selectedClaim.preauth_result.estimatedApprovalTime}</p>
                              )}
                              {selectedClaim.preauth_result.requiredDocumentation?.length > 0 && (
                                <p>📋 Required: {selectedClaim.preauth_result.requiredDocumentation.slice(0, 2).join(', ')}</p>
                              )}
                            </div>
                          )}
                          {selectedClaim.preauth_result.recommendation && (
                            <p className="text-xs text-slate-500 mt-1 italic">{selectedClaim.preauth_result.recommendation}</p>
                          )}
                        </div>
                      )}

                      {/* Benefits Verification */}
                      {selectedClaim.benefits_result && (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-semibold text-slate-700">Benefits Verification</span>
                            {selectedClaim.benefits_result.skipped ? (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500">Skipped</span>
                            ) : selectedClaim.benefits_result.error ? (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-600">Error</span>
                            ) : (
                              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${selectedClaim.benefits_result.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                {selectedClaim.benefits_result.status || 'Checked'}
                              </span>
                            )}
                          </div>
                          {selectedClaim.benefits_result.skipped && (
                            <p className="text-xs text-slate-500">{selectedClaim.benefits_result.reason}</p>
                          )}
                          {selectedClaim.benefits_result.error && (
                            <p className="text-xs text-red-500">{selectedClaim.benefits_result.error}</p>
                          )}
                          {!selectedClaim.benefits_result.skipped && !selectedClaim.benefits_result.error && (
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-600">
                              {selectedClaim.benefits_result.remainingBenefit != null && (
                                <p>💰 Remaining benefit: <strong>${selectedClaim.benefits_result.remainingBenefit}</strong></p>
                              )}
                              {selectedClaim.benefits_result.deductible != null && (
                                <p>🔖 Deductible remaining: <strong>${selectedClaim.benefits_result.deductible}</strong></p>
                              )}
                              {selectedClaim.benefits_result.coinsurance != null && (
                                <p>📊 Coinsurance: <strong>{selectedClaim.benefits_result.coinsurance}</strong></p>
                              )}
                              {selectedClaim.benefits_result.maxBenefit != null && (
                                <p>📋 Max benefit: <strong>${selectedClaim.benefits_result.maxBenefit}</strong></p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-3">Financial Summary</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Claim Amount</p>
                    <p className="text-lg font-bold text-slate-900">{formatCurrency(selectedClaim.claim_amount)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Paid Amount</p>
                    <p className="text-lg font-bold text-green-700">{formatCurrency(selectedClaim.claim_paid_amount)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Patient Responsibility</p>
                    {selectedClaim.claim_status === 'Denied' && !selectedClaim.patient_responsibility
                      ? <p className="text-sm font-medium text-slate-400 mt-1">Pending appeal</p>
                      : <p className="text-lg font-bold text-orange-600">{formatCurrency(selectedClaim.patient_responsibility)}</p>
                    }
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Service Date</p>
                  <p className="font-medium text-slate-900">{formatDate(selectedClaim.service_date)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Date Sent</p>
                  <p className="font-medium text-slate-900">{formatDate(selectedClaim.date_sent)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Date Received</p>
                  <p className="font-medium text-slate-900">{formatDate(selectedClaim.date_received)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Date Paid</p>
                  <p className="font-medium text-slate-900">{formatDate(selectedClaim.date_paid)}</p>
                </div>
              </div>

              {selectedClaim.procedure_codes && (
                <div>
                  <p className="text-sm text-slate-500 mb-2">Procedure Codes</p>
                  <div className="flex flex-wrap gap-2">
                    {(Array.isArray(selectedClaim.procedure_codes)
                      ? selectedClaim.procedure_codes
                      : String(selectedClaim.procedure_codes).split(',')
                    ).map((code, idx) => (
                      <span key={idx} className="px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-mono">{code.trim()}</span>
                    ))}
                  </div>
                </div>
              )}

              {selectedClaim.diagnosis_codes && (
                <div>
                  <p className="text-sm text-slate-500 mb-2">Diagnosis Codes</p>
                  <div className="flex flex-wrap gap-2">
                    {(Array.isArray(selectedClaim.diagnosis_codes)
                      ? selectedClaim.diagnosis_codes
                      : String(selectedClaim.diagnosis_codes).split(',')
                    ).map((code, idx) => (
                      <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-mono">{code.trim()}</span>
                    ))}
                  </div>
                </div>
              )}

              {selectedClaim.claim_status === 'Denied' && (
                <div data-tour="denial-info" className="bg-red-50 rounded-lg p-4 border border-red-200">
                  <h3 className="font-semibold text-red-900 mb-2">Denial Information</h3>
                  <div className="flex flex-col gap-3">
                    <div>
                      <p className="text-sm text-red-600">Denial Code</p>
                      <p className="font-medium text-red-900">{selectedClaim.denial_code || 'Not specified'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-red-600">Denial Reason</p>
                      <p className="font-medium text-red-900">{selectedClaim.denial_reason || 'Not specified'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Portal Submission Required */}
              {selectedClaim.requires_manual_portal && (
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                  <h3 className="font-semibold text-purple-900 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> Manual Portal Submission Required
                  </h3>
                  <p className="text-sm text-purple-700 mb-3">
                    FlowRCM generated the appeal letter but this payer requires submission through their provider portal. The letter is ready below — copy it and paste it into the portal.
                  </p>
                  {selectedClaim.portal_url && (
                    <a
                      href={selectedClaim.portal_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-700"
                    >
                      Open Payer Portal
                    </a>
                  )}
                </div>
              )}

              {/* Appeal Tracking Panel */}
              {selectedClaim.appeal_submitted_date && (
                <div data-tour="appeal-section" className="bg-teal-50 rounded-lg p-4 border border-teal-200">
                  <h3 className="font-semibold text-teal-900 mb-3 flex items-center gap-2">
                    <Send className="w-4 h-4" /> Appeal Tracking
                  </h3>

                  {apiService.getAppMode() === 'demo' ? (
                    /* Demo mode: show fully automatic view — no manual submit buttons */
                    <>
                      {selectedClaim.appeal_status === 'Approved' ? (
                        <div className="flex items-center gap-3 bg-green-50 border border-green-300 rounded-lg px-4 py-3 mb-3" style={{ animation: 'demoBadgeIn 0.4s ease-out' }}>
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-sm font-bold text-green-800">Appeal Approved — Paid ${selectedClaim.claim_paid_amount}</p>
                            <p className="text-xs text-green-600">
                              Submitted {formatDate(selectedClaim.appeal_submitted_date)} · Approved {formatDate(selectedClaim.appeal_approved_date)} · {selectedClaim.payer_name || 'Delta Dental'} reversed the denial
                            </p>
                          </div>
                          <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-green-200 text-green-800">WON</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-3 mb-2">
                          <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-green-800">Auto-Submitted by FlowRCM</p>
                            <p className="text-xs text-green-600">Sent to {selectedClaim.payer_name || 'MetLife'} on {formatDate(selectedClaim.appeal_submitted_date)} · Awaiting decision</p>
                          </div>
                        </div>
                      )}
                      {selectedClaim.appeal_documents && selectedClaim.appeal_documents.length > 0 && (
                        <div className="mb-4 p-3 bg-white border border-teal-200 rounded-lg">
                          <p className="text-xs font-semibold text-teal-700 mb-2 uppercase tracking-wide">Bundled Attachments ({selectedClaim.appeal_documents.length} files)</p>
                          <div className="flex flex-col gap-1.5">
                            {selectedClaim.appeal_documents.map((doc, i) => (
                              <div key={i} className="flex items-center gap-2 text-sm text-slate-700">
                                <span className="text-teal-500">📎</span>
                                <span>{doc.name}</span>
                                <span className="text-xs text-slate-400 ml-auto">{doc.date}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {selectedClaim.appeal_letter && (
                        <div data-tour="appeal-letter" className="p-4 bg-white border border-teal-200 rounded-lg">
                          <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">
                            {selectedClaim.appeal_letter}
                          </pre>
                        </div>
                      )}
                    </>
                  ) : (
                    /* Production mode: full manual review + submit workflow */
                    <>
                      <div className="grid grid-cols-3 gap-4 mb-4">
                        <div>
                          <p className="text-sm text-teal-600">Submitted</p>
                          <p className="font-medium text-teal-900">{formatDate(selectedClaim.appeal_submitted_date)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-teal-600">Deadline</p>
                          <p className={`font-medium ${daysUntil(selectedClaim.appeal_deadline) <= 14 ? 'text-yellow-700' : 'text-teal-900'}`}>
                            {formatDate(selectedClaim.appeal_deadline)}
                            {daysUntil(selectedClaim.appeal_deadline) !== null && daysUntil(selectedClaim.appeal_deadline) >= 0 && (
                              <span className="text-xs ml-1 text-slate-500">({daysUntil(selectedClaim.appeal_deadline)}d left)</span>
                            )}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-teal-600">Appeal Status</p>
                          <div className="mt-1">{getAppealStatusBadge(selectedClaim.appeal_status)}</div>
                        </div>
                      </div>
                      <div className="mb-3">
                        <label className="text-sm text-teal-700 font-medium block mb-1">Update Outcome</label>
                        <select
                          value={selectedClaim.appeal_status || 'Submitted'}
                          onChange={(e) => handleUpdateAppealStatus(e.target.value)}
                          disabled={statusUpdating}
                          className="w-full px-3 py-2 border border-teal-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white disabled:opacity-60"
                        >
                          <option value="Submitted">Submitted — Awaiting Decision</option>
                          <option value="Approved">Approved — Claim Paid</option>
                          <option value="Partially Approved">Partially Approved</option>
                          <option value="Denied">Denied — Appeal Unsuccessful</option>
                        </select>
                        {statusUpdating && <p className="text-xs text-teal-600 mt-1">Updating...</p>}
                        {(selectedClaim.appeal_status === 'Approved' || selectedClaim.appeal_status === 'Partially Approved') && (
                          <div className="mt-2">
                            <label className="text-sm text-teal-700 font-medium block mb-1">Amount Recovered ($)</label>
                            <input
                              type="number"
                              placeholder={selectedClaim.claim_paid_amount || '0.00'}
                              onBlur={(e) => {
                                const amt = parseFloat(e.target.value);
                                if (!isNaN(amt) && amt > 0) awsApi.updateAppealStatus(selectedClaim.id, selectedClaim.appeal_status, amt);
                              }}
                              className="w-full px-3 py-2 border border-teal-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                            />
                          </div>
                        )}
                      </div>
                      {selectedClaim.appeal_letter && (
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <button
                              onClick={() => setShowLetter(v => !v)}
                              className="flex items-center gap-2 text-sm font-medium text-teal-700 hover:text-teal-900 transition-colors"
                            >
                              {showLetter ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              {showLetter ? 'Hide' : 'View'} Appeal Letter
                            </button>
                            <button
                              onClick={() => { setShowFaxModal(true); setFaxResult(null); }}
                              className="flex items-center gap-2 text-sm font-medium px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors"
                            >
                              <Send className="w-3.5 h-3.5" /> Send via Fax
                            </button>
                          </div>
                          {showLetter && (
                            <div data-tour="appeal-letter" className="mt-3 p-4 bg-white border border-teal-200 rounded-lg">
                              <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">
                                {selectedClaim.appeal_letter}
                              </pre>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {/* Documents Section */}
                  <div className="mt-6 pt-6 border-t border-slate-100">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Paperclip className="w-4 h-4 text-teal-600" />
                        <h4 className="font-semibold text-slate-800 text-sm">Supporting Documents</h4>
                        {claimDocuments.length > 0 && (
                          <span className="bg-teal-50 text-teal-700 text-xs font-medium px-2 py-0.5 rounded-full">{claimDocuments.length}</span>
                        )}
                      </div>
                      <label className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-colors ${uploading ? 'bg-slate-100 text-slate-400' : 'bg-teal-50 text-teal-700 hover:bg-teal-100'}`}>
                        {uploading ? <><div className="w-3 h-3 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" /> Uploading...</> : <><Upload className="w-3 h-3" /> Attach File</>}
                        <input ref={docInputRef} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.tiff,.doc,.docx" onChange={handleDocumentUpload} disabled={uploading} />
                      </label>
                    </div>
                    {docsLoading ? (
                      <div className="text-xs text-slate-400 py-2">Loading documents...</div>
                    ) : claimDocuments.length === 0 ? (
                      <div className="text-xs text-slate-400 py-3 text-center border border-dashed border-slate-200 rounded-lg">
                        No documents attached. Attach X-rays, EOBs, or pre-auth letters to support appeals.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {claimDocuments.map(doc => (
                          <div key={doc.id} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg border border-slate-100">
                            <div className="flex items-center gap-2 min-w-0">
                              <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                              <div className="min-w-0">
                                <p className="text-xs font-medium text-slate-700 truncate">{doc.file_name}</p>
                                <p className="text-xs text-slate-400">{doc.document_type} · {doc.file_size ? (doc.file_size / 1024).toFixed(0) + ' KB' : '—'}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button onClick={() => handleDownloadDocument(doc)} className="p-1.5 text-slate-400 hover:text-teal-600 rounded transition-colors" title="Download">
                                <Download className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={() => handleDeleteDocument(doc.id)} className="p-1.5 text-slate-400 hover:text-red-500 rounded transition-colors" title="Delete">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Fax Modal */}
                  {showFaxModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                      <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
                        <h3 className="text-lg font-bold text-slate-900 mb-1">Send Appeal via Fax</h3>
                        <p className="text-sm text-slate-500 mb-4">
                          Fax the appeal letter directly to {selectedClaim.payer || 'the insurance company'}.
                        </p>
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Payer Fax Number
                          </label>
                          <input
                            type="text"
                            value={faxNumber}
                            onChange={(e) => setFaxNumber(e.target.value)}
                            placeholder="e.g. 1-800-555-1234 (leave blank to auto-lookup)"
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          />
                          <p className="text-xs text-slate-400 mt-1">
                            Leave blank to auto-lookup from our payer database. Always verify fax number with the payer before sending.
                          </p>
                        </div>
                        {faxResult && (
                          <div className={`p-3 rounded-lg mb-4 text-sm ${faxResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                            {faxResult.message}
                          </div>
                        )}
                        <div className="flex gap-3">
                          <button
                            onClick={() => { setShowFaxModal(false); setFaxResult(null); }}
                            className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors text-sm font-medium"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleSendFax}
                            disabled={faxLoading}
                            className="flex-1 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors text-sm font-medium disabled:opacity-60 flex items-center justify-center gap-2"
                          >
                            {faxLoading ? (
                              <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Sending...</>
                            ) : (
                              <><Send className="w-4 h-4" /> Send Fax</>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-slate-200 flex items-center justify-between gap-3">
              {submitResult && (
                <div className={`text-sm px-3 py-1 rounded-lg ${submitResult.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {submitResult.message}
                </div>
              )}
              {!['Submitted', 'Paid'].includes(selectedClaim.claim_status) && !selectedClaim.appeal_submitted_date && (
                <div className="flex items-center gap-2">
                  <div className="relative group">
                    <button
                      onClick={handleSubmitToInsurance}
                      disabled={submitLoading}
                      className={`flex items-center gap-2 px-5 py-2 text-white rounded-lg font-semibold transition-colors disabled:opacity-60 ${selectedClaim.claim_status === 'AI Flagged' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                    >
                      {submitLoading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Submitting...
                        </>
                      ) : selectedClaim.claim_status === 'AI Flagged' ? (
                        <><AlertTriangle className="w-4 h-4" /> Review &amp; Submit</>
                      ) : (
                        <><Upload className="w-4 h-4" /> Submit to Insurance</>
                      )}
                    </button>
                    <div className="absolute bottom-full left-0 mb-2 w-64 bg-slate-800 text-white text-xs rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                      Claims are submitted automatically via EDI. Use this button only if you need to manually override the automation for this specific claim.
                    </div>
                  </div>
                  <button
                    onClick={handleDryRun}
                    disabled={submitLoading}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-lg font-semibold transition-colors disabled:opacity-60 text-sm"
                    title="Generate the 837D EDI file and log it to CloudWatch without actually submitting"
                  >
                    Dry Run
                  </button>
                </div>
              )}
              {selectedClaim.claim_status === 'Denied' && !selectedClaim.appeal_submitted_date && (
                <button
                  data-tour="appeal-button"
                  onClick={handleSubmitAppeal}
                  disabled={appealLoading}
                  className="flex items-center gap-2 px-5 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-60"
                >
                  {appealLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Generating Appeal...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" /> Submit Appeal
                    </>
                  )}
                </button>
              )}
              {!['Written Off', 'Paid', 'Applied'].includes(selectedClaim.claim_status) && apiService.getAppMode() !== 'demo' && (
                <button
                  onClick={() => setShowWriteOff(w => !w)}
                  className="px-4 py-2 border border-red-200 text-red-600 rounded-lg font-medium hover:bg-red-50 transition-colors text-sm"
                >
                  Write Off
                </button>
              )}
              <button onClick={handleCloseModal} className="ml-auto px-6 py-2 border border-slate-300 rounded-lg font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
                Close
              </button>
            </div>

            {/* Write-off form */}
            {showWriteOff && (
              <div className="px-6 pb-6 pt-0 border-t border-slate-100 bg-red-50">
                <p className="text-sm font-semibold text-red-700 mt-4 mb-3">Write Off This Claim</p>
                <div className="flex gap-3 flex-wrap">
                  <input
                    type="number"
                    placeholder="Amount to write off"
                    value={writeOffAmount}
                    onChange={e => setWriteOffAmount(e.target.value)}
                    className="px-3 py-2 border border-red-200 rounded-lg text-sm bg-white w-40"
                  />
                  <input
                    type="text"
                    placeholder="Reason (e.g. contractual adjustment)"
                    value={writeOffReason}
                    onChange={e => setWriteOffReason(e.target.value)}
                    className="px-3 py-2 border border-red-200 rounded-lg text-sm bg-white flex-1 min-w-48"
                  />
                  <button
                    onClick={handleWriteOff}
                    disabled={writeOffLoading || !writeOffAmount}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-60"
                  >
                    {writeOffLoading ? 'Saving...' : 'Confirm Write-Off'}
                  </button>
                  <button onClick={() => setShowWriteOff(false)} className="px-4 py-2 border border-red-200 text-red-600 rounded-lg text-sm hover:bg-white transition-colors">
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div></div>
        </div>
      )}
      <style>{`
        @keyframes demoBadgeIn {
          from { opacity: 0; transform: translateY(6px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0)   scale(1); }
        }
        @keyframes pulseGlowPurple {
          0%   { box-shadow: 0 0 0 0    rgba(147,51,234,0.35); }
          70%  { box-shadow: 0 0 0 10px rgba(147,51,234,0); }
          100% { box-shadow: 0 0 0 0    rgba(147,51,234,0); }
        }
      `}</style>
    </div>
  );
};

export default Claims;
